// =============================================
// COMPONENTE: ExecutionDashboard
// =============================================
// Arquivo: ExecutionDashboard.tsx
// Descrição: Dashboard detalhado de execuções de automação
// =============================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  MessageSquare,
  User,
  Calendar,
  Eye,
  X,
  Zap,
  Pause
} from 'lucide-react';
import { apiService } from '../../services/api';
import './ExecutionDashboard.css';

// =============================================
// INTERFACES
// =============================================

interface ExecutionMessage {
  id: number;
  tipo: 'sent' | 'received';
  conteudo: string;
  timestamp: string;
  node_id?: string;
  node_type?: string;
}

interface Execution {
  id: number;
  flow_id: number;
  flow_nome: string;
  contato_id: string;
  contato_nome?: string;
  status: 'running' | 'waiting_input' | 'completed' | 'failed' | 'cancelled';
  iniciado_em: string;
  finalizado_em?: string;
  duracao_segundos?: number;
  current_node_id?: string;
  current_node_type?: string;
  mensagens?: ExecutionMessage[];
  erro?: string;
  context?: Record<string, unknown>;
}

interface ExecutionFilters {
  status?: string;
  flow_id?: number;
  contato?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface Flow {
  id: number;
  nome: string;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const ExecutionDashboard: React.FC = () => {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ExecutionFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null);
  const [expandedExecutions, setExpandedExecutions] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // =============================================
  // CARREGAR DADOS
  // =============================================

  const loadExecutions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');

      if (filters.status) params.append('status', filters.status);
      if (filters.flow_id) params.append('flow_id', filters.flow_id.toString());
      if (filters.contato) params.append('contato', filters.contato);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (searchTerm) params.append('search', searchTerm);

      const paramsObj: Record<string, any> = {
        page,
        limit: 20,
        ...filters,
        ...(searchTerm ? { search: searchTerm } : {})
      };

      const response = await apiService.listAutomationExecutions(paramsObj);
      if (response.success) {
        setExecutions(response.data || []);
        setTotalPages((response as any).totalPages || 1);
      }
    } catch (error) {
      // Erro tratado pelo apiService
    } finally {
      setLoading(false);
    }
  }, [page, filters, searchTerm]);

  const loadFlows = async () => {
    try {
      const response = await apiService.listAutomationFlows();
      if (response.success) {
        setFlows(response.data || []);
      }
    } catch (error) {
      // Erro tratado pelo apiService
    }
  };

  const loadExecutionDetails = async (executionId: number) => {
    try {
      const response = await apiService.getAutomationExecution(executionId);
      if (response.success) {
        setSelectedExecution(response.data);
      }
    } catch (error) {
      // Erro tratado pelo apiService
    }
  };

  // =============================================
  // EFFECTS
  // =============================================

  useEffect(() => {
    loadExecutions();
    loadFlows();
  }, [loadExecutions]);

  // Auto-refresh a cada 10 segundos
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadExecutions();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, loadExecutions]);

  // =============================================
  // HANDLERS
  // =============================================

  const handleFilterChange = (key: keyof ExecutionFilters, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setPage(1);
  };

  const toggleExpand = (executionId: number) => {
    setExpandedExecutions(prev => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  };

  const cancelExecution = async (executionId: number) => {
    if (!window.confirm('Deseja realmente cancelar esta execução?')) return;

    try {
      const response = await apiService.cancelAutomationExecution(executionId);
      if (response.success) {
        loadExecutions();
      }
    } catch (error) {
      // Erro tratado pelo apiService
    }
  };

  // =============================================
  // UTILITÁRIOS
  // =============================================

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactNode; color: string; label: string; bg: string }> = {
      running: {
        icon: <Play size={14} />,
        color: '#3B82F6',
        label: 'Em Execução',
        bg: 'rgba(59, 130, 246, 0.1)'
      },
      waiting_input: {
        icon: <Pause size={14} />,
        color: '#F59E0B',
        label: 'Aguardando Input',
        bg: 'rgba(245, 158, 11, 0.1)'
      },
      completed: {
        icon: <CheckCircle size={14} />,
        color: '#10B981',
        label: 'Concluído',
        bg: 'rgba(16, 185, 129, 0.1)'
      },
      failed: {
        icon: <XCircle size={14} />,
        color: '#EF4444',
        label: 'Falha',
        bg: 'rgba(239, 68, 68, 0.1)'
      },
      cancelled: {
        icon: <AlertTriangle size={14} />,
        color: '#6B7280',
        label: 'Cancelado',
        bg: 'rgba(107, 114, 128, 0.1)'
      }
    };

    return configs[status] || configs.running;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="execution-dashboard">
      {/* HEADER */}
      <div className="execution-header">
        <div className="header-title">
          <Zap size={24} />
          <h2>Execuções de Automação</h2>
          <span className="execution-count">{executions.length} execuções</span>
        </div>

        <div className="header-actions">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar por contato ou fluxo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filtros
            {Object.keys(filters).length > 0 && (
              <span className="filter-badge">{Object.keys(filters).length}</span>
            )}
          </button>

          <button
            className={`auto-refresh-toggle ${autoRefresh ? 'active' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Auto-refresh ativo' : 'Auto-refresh desativado'}
          >
            <RefreshCw size={16} className={autoRefresh ? 'spinning' : ''} />
          </button>

          <button className="refresh-btn" onClick={loadExecutions} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Atualizar
          </button>
        </div>
      </div>

      {/* FILTROS */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            >
              <option value="">Todos</option>
              <option value="running">Em Execução</option>
              <option value="waiting_input">Aguardando Input</option>
              <option value="completed">Concluído</option>
              <option value="failed">Falha</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Fluxo</label>
            <select
              value={filters.flow_id || ''}
              onChange={(e) => handleFilterChange('flow_id', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Todos</option>
              {flows.map(flow => (
                <option key={flow.id} value={flow.id}>{flow.nome}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Data Início</label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
            />
          </div>

          <div className="filter-group">
            <label>Data Fim</label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
            />
          </div>

          <button className="clear-filters" onClick={handleClearFilters}>
            <X size={14} />
            Limpar
          </button>
        </div>
      )}

      {/* LISTA DE EXECUÇÕES */}
      <div className="executions-list">
        {loading && executions.length === 0 ? (
          <div className="loading-state">
            <RefreshCw size={40} className="spinning" />
            <p>Carregando execuções...</p>
          </div>
        ) : executions.length === 0 ? (
          <div className="empty-state">
            <Zap size={48} />
            <h3>Nenhuma execução encontrada</h3>
            <p>As execuções de automação aparecerão aqui</p>
          </div>
        ) : (
          executions.map(execution => {
            const statusConfig = getStatusConfig(execution.status);
            const isExpanded = expandedExecutions.has(execution.id);

            return (
              <div key={execution.id} className={`execution-card ${execution.status}`}>
                <div className="execution-main" onClick={() => toggleExpand(execution.id)}>
                  <div className="execution-status" style={{ background: statusConfig.bg }}>
                    <span style={{ color: statusConfig.color }}>
                      {statusConfig.icon}
                    </span>
                  </div>

                  <div className="execution-info">
                    <div className="execution-title">
                      <span className="flow-name">{execution.flow_nome}</span>
                      <span className="execution-id">#{execution.id}</span>
                    </div>
                    <div className="execution-contact">
                      <User size={12} />
                      <span>{formatPhone(execution.contato_id)}</span>
                      {execution.contato_nome && (
                        <>
                          <span className="contact-separator">→</span>
                          <span className="contact-name">{execution.contato_nome}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="execution-meta">
                    <div className="meta-item">
                      <Calendar size={12} />
                      <span>{formatDate(execution.iniciado_em)}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={12} />
                      <span>{formatDuration(execution.duracao_segundos)}</span>
                    </div>
                  </div>

                  <div
                    className="execution-status-badge"
                    style={{ background: statusConfig.bg, color: statusConfig.color }}
                  >
                    {statusConfig.icon}
                    {statusConfig.label}
                  </div>

                  <div className="execution-actions">
                    <button
                      className="action-btn view"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadExecutionDetails(execution.id);
                      }}
                      title="Ver detalhes"
                    >
                      <Eye size={16} />
                    </button>

                    {(execution.status === 'running' || execution.status === 'waiting_input') && (
                      <button
                        className="action-btn cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelExecution(execution.id);
                        }}
                        title="Cancelar execução"
                      >
                        <X size={16} />
                      </button>
                    )}

                    <button className={`expand-btn ${isExpanded ? 'expanded' : ''}`}>
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </div>

                {/* DETALHES EXPANDIDOS */}
                {isExpanded && (
                  <div className="execution-details">
                    {execution.current_node_id && (
                      <div className="detail-row">
                        <span className="detail-label">Nó Atual:</span>
                        <span className="detail-value">
                          {execution.current_node_id} ({execution.current_node_type})
                        </span>
                      </div>
                    )}

                    {execution.erro && (
                      <div className="detail-row error">
                        <span className="detail-label">Erro:</span>
                        <span className="detail-value">{execution.erro}</span>
                      </div>
                    )}

                    {execution.mensagens && execution.mensagens.length > 0 && (
                      <div className="messages-preview">
                        <h4>
                          <MessageSquare size={14} />
                          Últimas Mensagens
                        </h4>
                        <div className="messages-list">
                          {execution.mensagens.slice(-3).map((msg, idx) => (
                            <div key={idx} className={`message ${msg.tipo}`}>
                              <span className="message-type">
                                {msg.tipo === 'sent' ? 'Bot' : 'Usuário'}
                              </span>
                              <span className="message-content">{msg.conteudo}</span>
                              <span className="message-time">
                                {new Date(msg.timestamp).toLocaleTimeString('pt-BR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* PAGINAÇÃO */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Anterior
          </button>
          <span>Página {page} de {totalPages}</span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Próxima
          </button>
        </div>
      )}

      {/* MODAL DE DETALHES */}
      {selectedExecution && (
        <div className="execution-modal-overlay" onClick={() => setSelectedExecution(null)}>
          <div className="execution-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Zap size={20} />
                Detalhes da Execução #{selectedExecution.id}
              </h3>
              <button onClick={() => setSelectedExecution(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-content">
              <div className="modal-section">
                <h4>Informações Gerais</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Fluxo</label>
                    <span>{selectedExecution.flow_nome}</span>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <span
                      className="status-badge"
                      style={{
                        background: getStatusConfig(selectedExecution.status).bg,
                        color: getStatusConfig(selectedExecution.status).color
                      }}
                    >
                      {getStatusConfig(selectedExecution.status).icon}
                      {getStatusConfig(selectedExecution.status).label}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Contato</label>
                    <span>
                      <User size={12} />
                      {formatPhone(selectedExecution.contato_id)}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Início</label>
                    <span>{formatDate(selectedExecution.iniciado_em)}</span>
                  </div>
                  {selectedExecution.finalizado_em && (
                    <div className="info-item">
                      <label>Fim</label>
                      <span>{formatDate(selectedExecution.finalizado_em)}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Duração</label>
                    <span>{formatDuration(selectedExecution.duracao_segundos)}</span>
                  </div>
                </div>
              </div>

              {selectedExecution.erro && (
                <div className="modal-section error-section">
                  <h4>
                    <AlertTriangle size={16} />
                    Erro
                  </h4>
                  <div className="error-message">{selectedExecution.erro}</div>
                </div>
              )}

              {selectedExecution.mensagens && selectedExecution.mensagens.length > 0 && (
                <div className="modal-section">
                  <h4>
                    <MessageSquare size={16} />
                    Histórico de Mensagens
                  </h4>
                  <div className="message-history">
                    {selectedExecution.mensagens.map((msg, idx) => (
                      <div key={idx} className={`history-message ${msg.tipo}`}>
                        <div className="message-header">
                          <span className="message-sender">
                            {msg.tipo === 'sent' ? (
                              <>
                                <Zap size={12} />
                                Bot
                              </>
                            ) : (
                              <>
                                <User size={12} />
                                Usuário
                              </>
                            )}
                          </span>
                          <span className="message-timestamp">
                            {new Date(msg.timestamp).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <div className="message-body">{msg.conteudo}</div>
                        {msg.node_type && (
                          <div className="message-node">
                            Nó: {msg.node_type}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedExecution.context && Object.keys(selectedExecution.context).length > 0 && (
                <div className="modal-section">
                  <h4>Contexto</h4>
                  <pre className="context-preview">
                    {JSON.stringify(selectedExecution.context, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionDashboard;
